import api from './api';
import { tokenService } from './tokenService';

/**
 * Storefront pro Mandant (Marktplatz-Rework T7, Plan 3.8).
 * Öffentlicher Teil: ohne Login (`/api/public/storefront/{slug}`), nur ein
 * vorhandener, gültiger Token wird als Bearer mitgeschickt, damit eingeloggte
 * Käufer:innen ohne neues Konto kaufen. Verwaltung: `/api/storefront/*`.
 */

const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PUBLIC_BASE = `${baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`}/public/storefront`;

export interface StorefrontFeature { title: string; description: string; icon: string }
export interface StorefrontFaqItem { question: string; answer: string }
export interface StorefrontTestimonial { quote: string; author: string; role: string }

export interface PublicStorefront {
    slug: string;
    displayName: string | null;
    heroTitle: string | null;
    heroSubtitle: string | null;
    heroDescription: string | null;
    heroImageUrl: string | null;
    features: StorefrontFeature[];
    faq: StorefrontFaqItem[];
    testimonials: StorefrontTestimonial[];
    allowGuestCheckout: boolean;
    allowFreeEnrollment: boolean;
    termsUrl: string | null;
    privacyPolicyUrl: string | null;
    customCss: string | null;
    seoTitle: string | null;
    seoDescription: string | null;
    branding: { logoUrl: string | null; primaryColor: string | null; secondaryColor: string | null };
}

export interface PublicProduct {
    id: number;
    title: string;
    shortDescription: string | null;
    description?: string | null;
    thumbnailUrl: string | null;
    isFeatured: boolean;
    position: number;
    accessModel: 'lifetime' | 'time_limited';
    accessDurationDays: number | null;
    price: { amount: string; currency: string; isFree: boolean; grossIncludesVat: boolean } | null;
}

export interface StorefrontCheckoutPayload {
    productIds: number[];
    email?: string;
    firstName?: string;
    lastName?: string;
    password?: string;
    billingAddress?: { name?: string; street: string; zip: string; city: string; country: string };
    vatId?: string;
    acceptTerms: boolean;
    acceptImmediateAccess?: boolean;
}

export interface StorefrontCheckoutResult {
    success: boolean;
    orderId: string;
    orderNumber: string;
    status: 'requires_payment' | 'completed';
    paypalOrderId: string | null;
    total: string;
    currency: string;
    isFree: boolean;
    customerEmail: string;
}

export interface StorefrontPaymentConfig {
    clientId: string;
    env: string;
    currency: string;
    configured: boolean;
    sellerMerchantId: string | null;
    partnerAttributionId: string | null;
    sellerCanReceivePayments: boolean;
}

export interface StorefrontOrderStatus {
    orderId: string;
    orderNumber: string;
    status: string;
    paymentStatus: string;
    total: string;
    currency: string;
    paidAt: string | null;
    outcome?: string;
}

export class StorefrontApiError extends Error {
    constructor(message: string, public status: number) {
        super(message);
    }
}

async function publicRequest<T>(path: string, init?: RequestInit): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (init?.body) headers['Content-Type'] = 'application/json';
    if (init?.method === 'POST' && tokenService.hasValidToken()) {
        headers.Authorization = `Bearer ${tokenService.getToken()}`;
    }
    const response = await fetch(`${PUBLIC_BASE}${path}`, { ...init, headers });
    let body: any = null;
    try {
        body = await response.json();
    } catch {
        /* leere Antwort */
    }
    if (!response.ok) {
        throw new StorefrontApiError(body?.error || body?.message || body?.detail || `HTTP ${response.status}`, response.status);
    }
    return body as T;
}

export const publicStorefrontApi = {
    get: (slug: string) =>
        publicRequest<{ storefront: PublicStorefront; featured: PublicProduct[]; productCount: number }>(`/${encodeURIComponent(slug)}`),
    products: (slug: string) => publicRequest<{ products: PublicProduct[] }>(`/${encodeURIComponent(slug)}/products`),
    product: (slug: string, id: number) => publicRequest<{ product: PublicProduct }>(`/${encodeURIComponent(slug)}/products/${id}`),
    paymentConfig: (slug: string) => publicRequest<StorefrontPaymentConfig>(`/${encodeURIComponent(slug)}/payment-config`),
    checkout: (slug: string, payload: StorefrontCheckoutPayload) =>
        publicRequest<StorefrontCheckoutResult>(`/${encodeURIComponent(slug)}/checkout`, { method: 'POST', body: JSON.stringify(payload) }),
    capture: (slug: string, orderId: string, paypalOrderId: string, email: string) =>
        publicRequest<StorefrontOrderStatus>(`/${encodeURIComponent(slug)}/orders/${orderId}/capture`, {
            method: 'POST',
            body: JSON.stringify({ paypalOrderId, email }),
        }),
    order: (slug: string, orderId: string, email: string) =>
        publicRequest<StorefrontOrderStatus>(`/${encodeURIComponent(slug)}/orders/${orderId}?email=${encodeURIComponent(email)}`),
    /** Alte `/p/{slug}`-Links → Storefront-Slug des Mandanten (301-Ersatz). */
    legacyLandingPage: (landingSlug: string) =>
        publicRequest<{ slug: string }>(`/_legacy/landing-page/${encodeURIComponent(landingSlug)}`),
};

// ---- Verwaltung (Login, storefront.manage) --------------------------------

export interface StorefrontConfigDto {
    publicSlug: string;
    enabled: boolean;
    displayName: string | null;
    heroTitle: string | null;
    heroSubtitle: string | null;
    heroDescription: string | null;
    heroImageUrl: string | null;
    features: StorefrontFeature[];
    faq: StorefrontFaqItem[];
    testimonials: StorefrontTestimonial[];
    allowGuestCheckout: boolean;
    allowFreeEnrollment: boolean;
    termsUrl: string | null;
    privacyPolicyUrl: string | null;
    customCss: string | null;
    seoTitle: string | null;
    seoDescription: string | null;
}

export interface StorefrontAdminProduct {
    id: number;
    title: string;
    status: 'draft' | 'published' | 'archived';
    isActive: boolean;
    isFeatured: boolean;
    position: number;
    thumbnailUrl: string | null;
    price: { amount: string; currency: string } | null;
}

export const storefrontAdminApi = {
    getConfig: async (): Promise<{ config: StorefrontConfigDto | null; suggestedSlug?: string }> =>
        (await api.get('/storefront/config')).data,
    saveConfig: async (config: Partial<StorefrontConfigDto>): Promise<StorefrontConfigDto> =>
        (await api.put('/storefront/config', config)).data.config,
    products: async (): Promise<StorefrontAdminProduct[]> => (await api.get('/storefront/products')).data.products,
    patchProduct: async (id: number, patch: Partial<Pick<StorefrontAdminProduct, 'status' | 'isFeatured' | 'position'>>) =>
        (await api.patch(`/storefront/products/${id}`, patch)).data,
    reorder: async (ids: number[]) => (await api.put('/storefront/products/order', { ids })).data,
};

/* ------------------------------------------------------------------ */
/* Embed-Widget (T8, Plan 3.9)                                         */
/* ------------------------------------------------------------------ */

export type EmbedWidgetType = 'catalog' | 'course_card' | 'buy_button' | 'landing';
export type EmbedTheme = 'light' | 'dark' | 'auto';

export interface EmbedToken {
    id: number;
    label: string;
    widgetType: EmbedWidgetType;
    allowedOrigins: string[];
    config: { theme?: EmbedTheme; productId?: number };
    status: 'active' | 'revoked';
    tokenPreview: string;
    /** Nur bei Anlage und `reveal` gesetzt. */
    token?: string;
    lastUsedAt: string | null;
    revokedAt: string | null;
    createdAt: string;
}

export interface EmbedTokenPayload {
    label: string;
    widgetType: EmbedWidgetType;
    allowedOrigins: string[];
    config: { theme: EmbedTheme; productId?: number };
}

export const embedTokenApi = {
    list: async (): Promise<EmbedToken[]> => (await api.get('/storefront/embed-tokens')).data.tokens,
    create: async (payload: EmbedTokenPayload): Promise<EmbedToken> => (await api.post('/storefront/embed-tokens', payload)).data.token,
    update: async (id: number, patch: Partial<Pick<EmbedTokenPayload, 'label' | 'allowedOrigins' | 'config'>>): Promise<EmbedToken> =>
        (await api.patch(`/storefront/embed-tokens/${id}`, patch)).data.token,
    reveal: async (id: number): Promise<EmbedToken> => (await api.post(`/storefront/embed-tokens/${id}/reveal`)).data.token,
    revoke: async (id: number): Promise<EmbedToken> => (await api.post(`/storefront/embed-tokens/${id}/revoke`)).data.token,
    /** HTML-Fragment des Widgets für die Vorschau (nur eingeloggt, ohne Origin-Prüfung). */
    preview: async (id: number, theme: EmbedTheme, lang: string): Promise<string> =>
        (await api.get(`/storefront/embed-tokens/${id}/preview`, { params: { theme, lang }, responseType: 'text', transformResponse: (r) => r })).data,
};
